"use client";

import React from "react";
import { Button } from "../common/button";

export interface TherapistTier {
  title: string;
  priceRange: string;
  priceSuffix?: string;
  description: string;
  idealFor: string;
  skills: string;
  qualifications: string[];
  ctaText: string;
  onCtaClick?: () => void;
}

export interface TherapistTiersSectionProps {
  title?: string;
  subtitle?: string;
  tiers?: TherapistTier[];
}

export function TherapistTiersSection({
  title = "Therapist Tiers",
  subtitle = "Choose a therapist who understands your unique story, decide on a rate that fits your lifestyle, and begin a journey towards personal growth.",
  tiers = [
    {
      title: "Intern Therapists",
      priceRange: "$45 - $105",
      priceSuffix: "Per 50 Minute Session",
      description: "An Intern therapist at Alli is a Canadian master's student completing their clinical practicum.\n\nIf you're navigating everyday challenges, our Intern Therapists stand ready.\n\nThey're skilled in addressing mild anxieties, life transitions, self-discovery, and general mental health.\n\nIdeal for those seeking insights on self-development, relationships, and career shifts.\n\nWith our Intern Therapists, you're in a nurturing space designed for growth.",
      idealFor: "",
      skills: "",
      qualifications: [],
      ctaText: "Find A Therapist"
    },
    {
      title: "Associate Therapists",
      priceRange: "$115 - $175",
      priceSuffix: "Per 50 Minute Session",
      description: "Our Associate therapists are licensing and licensed professionals with a rich blend of experience.\n\nThey're equipped to support as life's challenges grow more complex.\n\nBeyond the foundational areas covered by our Residents, they delve into significant mental health challenges, including trauma, PTSD, and specific concerns like gender dysphoria or substance abuse.\n\nOpt for Associate if you're looking for a deeper, more comprehensive therapeutic journey.",
      idealFor: "",
      skills: "",
      qualifications: [],
      ctaText: "Find A Therapist"
    }
  ]
}: TherapistTiersSectionProps) {
  return (
    <section className="py-16 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
            {subtitle}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {tiers.map((tier, index) => (
            <div key={index} className="bg-white rounded-2xl p-8 shadow-sm">
              {/* Tier name first */}
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {tier.title}
              </h3>
              
              {/* Price */}
              <div className="mb-6">
                <p className="text-3xl font-bold text-primary-600">
                  {tier.priceRange}
                </p>
                {tier.priceSuffix && (
                  <p className="text-sm text-gray-600">{tier.priceSuffix}</p>
                )}
              </div>
              
              {/* Description with paragraph breaks */}
              <div className="mb-8 space-y-2">
                {tier.description.split('\n\n').map((paragraph, pIndex) => (
                  <p key={pIndex} className="text-gray-600 leading-relaxed text-sm">
                    {paragraph}
                  </p>
                ))}
              </div>
              
              {/* CTA Button */}
              <Button 
                className="w-full bg-primary-600 hover:bg-primary-700 text-white"
                onClick={tier.onCtaClick}
              >
                {tier.ctaText}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}